import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { DeleteResponseTypename, MutationTypename } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"

import {
  DeleteTeamDocument,
  DeleteTeamMutation,
  DeleteTeamMutationVariables
} from "../graphql/mutations.generated"
import { useTeamsLazyQuery } from "../graphql/queries.generated"
import { evictTeam } from "../lib/evictTeam"
import { readTeam } from "../lib/readTeam"
import { Team } from "../types/Team"

export function useDeleteTeam() {
  const { t } = useTranslation()
  const { cache } = useApolloClient()
  const [apolloMutate, rest] = useApolloMutation<
    DeleteTeamMutation,
    DeleteTeamMutationVariables
  >(DeleteTeamDocument)
  const [fetchTeams] = useTeamsLazyQuery()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Team.DELETE_TEAM.Start"),
      success: t("Request.Team.DELETE_TEAM.Success"),
      failure: t("Request.Team.DELETE_TEAM.Failure")
    }
  })

  const deleteTeam = useEvent(
    (
      variables: DeleteTeamMutationVariables,
      options?: { suppressToasts?: boolean }
    ): Promise<ApiResponse<Team | null>> => {
      evictTeam(variables.id, cache)
      const team = readTeam(variables.id, cache)
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: !!options?.suppressToasts
      })

      return callMutation(apolloMutate, {
        variables,
        transform: () => team,
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            team: { __typename: DeleteResponseTypename, success: true }
          },
          onCompleted: () => {
            evictTeam(variables.id, cache)
            raiseSuccessToast()
          },
          onError: () => {
            fetchTeams()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return {
    deleteTeam,
    ...rest
  }
}
